import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSocket } from '../providers/SocketProvider';
import { useToast } from '../components/ui/Toast';
import type {
  TransactionCreatedEvent,
  InventoryUpdatedEvent,
  LowStockAlertEvent,
} from '../lib/socket';

interface UseRealtimeUpdatesOptions {
  showNotifications?: boolean;
  autoRefresh?: boolean;
}

export function useRealtimeUpdates(options: UseRealtimeUpdatesOptions = {}) {
  const { showNotifications = true, autoRefresh = true } = options;

  const { socket, isConnected, connectionStatus, subscribe, reconnect } = useSocket();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation(['dashboard', 'common']);

  // Track if component is mounted to avoid state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Handle transaction created events
  const handleTransactionCreated = useCallback(
    (data: TransactionCreatedEvent) => {
      if (!isMountedRef.current) return;

      if (autoRefresh) {
        // Invalidate dashboard stats
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
        // Invalidate recent transactions
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'recent-transactions'] });
        // Invalidate sales trend if it's a sale
        if (data.type === 'sale') {
          queryClient.invalidateQueries({ queryKey: ['dashboard', 'sales-trend'] });
        }
      }

      if (showNotifications) {
        const typeLabels: Record<string, string> = {
          sale: t('common:invoice.types.sale'),
          purchase: t('common:invoice.types.purchase'),
          transfer: t('common:invoice.types.transfer'),
          issue: t('common:invoice.types.issue'),
          return: t('common:invoice.types.return'),
        };

        toast.info(
          t('realtime.transactionCreated'),
          `${typeLabels[data.type] || data.type}: ${data.invoiceNo}`
        );
      }
    },
    [autoRefresh, showNotifications, queryClient, toast, t]
  );

  // Handle inventory updated events
  const handleInventoryUpdated = useCallback(
    (_data: InventoryUpdatedEvent) => {
      if (!isMountedRef.current) return;

      if (autoRefresh) {
        // Invalidate low stock alerts
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'low-stock'] });
        // Invalidate out of stock alerts
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'out-of-stock'] });
        // Invalidate dashboard stats
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
      }
    },
    [autoRefresh, queryClient]
  );

  // Handle low stock alert events
  const handleLowStockAlert = useCallback(
    (data: LowStockAlertEvent) => {
      if (!isMountedRef.current) return;

      if (autoRefresh) {
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'low-stock'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
      }

      if (showNotifications) {
        toast.warning(
          t('realtime.lowStockAlert'),
          t('realtime.lowStockMessage', {
            item: data.itemName,
            qty: data.currentQty,
            min: data.minimumQty,
          })
        );
      }
    },
    [autoRefresh, showNotifications, queryClient, toast, t]
  );

  // Handle dashboard refresh events (forced refresh from server)
  const handleDashboardRefresh = useCallback(() => {
    if (!isMountedRef.current) return;

    if (autoRefresh) {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  }, [autoRefresh, queryClient]);

  // Subscribe to socket events
  useEffect(() => {
    if (!isConnected) return;

    const unsubscribes = [
      subscribe('transaction:created', handleTransactionCreated),
      subscribe('inventory:updated', handleInventoryUpdated),
      subscribe('inventory:low_stock', handleLowStockAlert),
      subscribe('dashboard:refresh', handleDashboardRefresh),
    ];

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [
    isConnected,
    subscribe,
    handleTransactionCreated,
    handleInventoryUpdated,
    handleLowStockAlert,
    handleDashboardRefresh,
  ]);

  return {
    isConnected,
    connectionStatus,
    reconnect,
    socket,
  };
}

// Hook for tracking connection status with retry logic
export function useConnectionStatus() {
  const { connectionStatus, reconnect } = useSocket();
  const reconnectAttemptsRef = useRef(0);
  const maxRetries = 3;

  const handleReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current < maxRetries) {
      reconnectAttemptsRef.current += 1;
      reconnect();
    }
  }, [reconnect]);

  // Reset attempts on successful connection
  useEffect(() => {
    if (connectionStatus === 'connected') {
      reconnectAttemptsRef.current = 0;
    }
  }, [connectionStatus]);

  return {
    status: connectionStatus,
    isConnected: connectionStatus === 'connected',
    isConnecting: connectionStatus === 'connecting',
    hasError: connectionStatus === 'error',
    canRetry: reconnectAttemptsRef.current < maxRetries,
    reconnect: handleReconnect,
  };
}
